import styles from "./PrivacyPolicy.module.scss";

export function CookiePolicyEN() {
  return (
    <div className={styles.policy}>
      <p>
        <strong>Last Updated: September 16, 2026</strong>
      </p>

      <p>
        Palamar Teknoloji Anonim Şirketi ("Palmate AI", "us", "we" or "our") uses cookies and similar technologies to help us remember
        details of your visits to our website at{" "}
        <a href="https://www.palmate.ai" target="_blank" rel="noopener noreferrer">
          www.palmate.ai
        </a>{" "}
        (the "Site") and to enhance your experience with our AI-powered chatbot solutions. This Cookie Policy explains
        how we use cookies, what types of cookies we deploy, and how you can manage your cookie preferences. It should
        be read with our Privacy Policy.
      </p>

      <h3>What are Cookies?</h3>
      <p>
        Cookies are small text files that are stored on your computer or mobile device when you visit a website. They
        help websites remember your actions and preferences over time, making your browsing experience more efficient
        and personalized. Cookies enable our Site to recognize your device and provide you with an improved user
        experience.
      </p>
      <p>
        When you interact with our Site, our web server may send cookies to your device. These cookies may contain data
        related to your visit and can help us:
      </p>
      <ul>
        <li>Remember your preferences and settings</li>
        <li>Understand how you use our services</li>
        <li>Provide personalized content and recommendations</li>
        <li>Improve the performance of our AI chatbot solutions</li>
      </ul>

      <h3>Do We Use Cookies and For What Purposes?</h3>
      <p>
        Yes, we use cookies to enhance your experience with our Site and AI chatbot services. We use cookies for the
        following purposes:
      </p>
      <p>
        <strong>Website Functionality</strong>: To identify users, process requests, remember user preferences, and
        allow you to navigate our Site without having to re-enter information when moving between pages or returning to
        the Site.
      </p>
      <p>
        <strong>Analytics and Performance</strong>: To collect and analyze Site usage data, user interaction patterns
        with our AI chatbots, and system performance metrics. This data helps us improve our services and enhance user
        experience.
      </p>
      <p>
        <strong>Personalization</strong>: To customize content based on your interests and previous interactions with
        our AI solutions, providing more relevant chatbot responses and service recommendations.
      </p>
      <p>
        <strong>Marketing and Advertising</strong>: We may place cookies on partner websites to advertise our AI chatbot
        services and solutions. These cookies help us display content tailored to your interests and evaluate the
        effectiveness of our marketing campaigns.
      </p>
      <p>
        <strong>Security</strong>: To protect our Site and services from fraudulent activities and ensure secure access
        to your account.
      </p>

      <h3>Types of Cookies We Use</h3>
      <p>
        <strong>By Source</strong>
      </p>
      <p>
        <strong>First-Party Cookies</strong>: Set directly by Palmate AI on our domain. They enable core website
        functionality, user authentication, and personalization features.
      </p>
      <p>
        <strong>Third-Party Cookies</strong>: Set by external service providers we integrate with, such as analytics
        platforms, advertising networks, and customer support tools.
      </p>
      <p>
        <strong>Note on Visitor Identification:</strong> We use third-party services that may place cookies on your
        device to identify visitors and associate your browsing activity with other personal information, including your
        email address. This information may be used to send you personalized communications and marketing. You can opt
        out of this tracking at{" "}
        <a href="https://app.retention.com/optout" target="_blank" rel="noopener noreferrer">
          https://app.retention.com/optout
        </a>
        . For GDPR-compliant opt-out, visit{" "}
        <a href="https://www.rb2b.com/rb2b-gdpr-opt-out" target="_blank" rel="noopener noreferrer">
          https://www.rb2b.com/rb2b-gdpr-opt-out
        </a>
        .
      </p>

      <p>
        <strong>By Duration</strong>
      </p>
      <p>
        <strong>Session Cookies</strong>: Temporary cookies deleted when you close your browser. Help with navigation
        and maintaining your session.
      </p>
      <p>
        <strong>Persistent Cookies</strong>: Remain on your device for a specified period or until deleted. Remember
        your preferences across visits.
      </p>

      <p>
        <strong>By Purpose</strong>
      </p>
      <p>
        <strong>Essential Cookies</strong>: Required for basic operation of our Site and AI services. Without these,
        certain features like secure login and chatbot interactions cannot function properly.
      </p>
      <p>
        <strong>Analytics Cookies</strong>: Help us understand how visitors interact with our Site and AI solutions.
      </p>
      <p>
        <strong>Personalization Cookies</strong>: Allow the Site to remember your choices and provide personalized
        experiences.
      </p>
      <p>
        <strong>Marketing Cookies</strong>: Used to deliver relevant advertising about our AI solutions and measure
        campaign effectiveness.
      </p>

      <h3>Cookie-free Measurement</h3>
      <p>
        Regardless of your cookie choice, we count page views on our own servers without cookies or any identifier stored on
        your device. We keep the page address, the referring website, campaign parameters, device type and language together
        with a daily-changing anonymous hash; IP addresses are not stored and nothing is shared with third parties. This
        measurement does not identify you and is not used for advertising.
      </p>
      <h3>Cookies We Use</h3>
      <p>The following table lists the main categories of cookies used on our Site:</p>
      <table>
        <thead>
          <tr>
            <th>Cookie Name</th>
            <th>Source</th>
            <th>Purpose</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>_palmate_session</td>
            <td>Palmate AI</td>
            <td>Essential - Session management</td>
            <td>Session</td>
          </tr>
          <tr>
            <td>_palmate_user</td>
            <td>Palmate AI</td>
            <td>Essential - User identification</td>
            <td>30 days</td>
          </tr>
          <tr>
            <td>_ga, _ga_*</td>
            <td>Google Analytics</td>
            <td>Analytics - Usage tracking</td>
            <td>1–2 years</td>
          </tr>
          <tr>
            <td>_gid</td>
            <td>Google Analytics</td>
            <td>Analytics - Session tracking</td>
            <td>24 hours</td>
          </tr>
          <tr>
            <td>_gat</td>
            <td>Google Analytics</td>
            <td>Analytics - Request throttling</td>
            <td>1 minute</td>
          </tr>
          <tr>
            <td>_gcl_au</td>
            <td>Google Ads</td>
            <td>Marketing - Conversion attribution</td>
            <td>90 days</td>
          </tr>
          <tr>
            <td>_fbp</td>
            <td>Meta Pixel</td>
            <td>Marketing - Browser identification and advertising measurement</td>
            <td>90 days</td>
          </tr>
          <tr>
            <td>_fbc</td>
            <td>Meta Pixel</td>
            <td>Marketing - Meta click identification and conversion attribution</td>
            <td>90 days</td>
          </tr>
          <tr>
            <td>_linkedin_tracking</td>
            <td>LinkedIn</td>
            <td>Marketing - Campaign measurement</td>
            <td>30 days</td>
          </tr>
          <tr>
            <td>hubspotutk</td>
            <td>HubSpot</td>
            <td>Marketing - Lead tracking</td>
            <td>13 months</td>
          </tr>
          <tr>
            <td>_clck, _clsk</td>
            <td>Microsoft Clarity</td>
            <td>Analytics - User behavior</td>
            <td>1 year</td>
          </tr>
          <tr>
            <td>__cf_bm</td>
            <td>Cloudflare</td>
            <td>Essential - Bot management</td>
            <td>30 minutes</td>
          </tr>
          <tr>
            <td>personalization_prefs</td>
            <td>Palmate AI</td>
            <td>Personalization - UI preferences</td>
            <td>1 year</td>
          </tr>
          {/* <tr>
            <td>reb2b</td>
            <td>RB2B / Retention.com</td>
            <td>Marketing - Visitor identification and tracking</td>
            <td>1 year</td>
          </tr> */}
        </tbody>
      </table>
      <p>
        <em>
          Note: This list includes the main cookies but is not exhaustive. Additional cookies may be set by third-party
          services integrated with our platform.
        </em>
      </p>

      <h3>Meta Pixel and Marketing Cookies</h3>
      <p>
        Meta Pixel (Facebook and Instagram) uses marketing cookies on palmate.ai only after you give marketing consent.
        The _fbp cookie identifies a browser for advertising measurement. The _fbc cookie stores a Meta click identifier
        when one is available in the page URL (fbclid), to link later events to that click; it is not set for every visitor.
        Both are first-party cookies on our domain. Their usual lifetime is 90 days and may be renewed on later visits;
        browser restrictions or deletion can shorten it.
      </p>
      <p>
        With consent, Pixel sends page views and successful demo, quote or contact submissions to Meta for campaign
        measurement and attribution. The Privacy Policy describes the event data and Meta's use of it. Cookie lifetimes
        are not the retention periods for information held by Meta. For the provider's cookie uses and controls, see the{" "}
        <a href="https://www.facebook.com/privacy/policies/cookies/" target="_blank" rel="noopener noreferrer">Meta Cookies Policy</a>.
      </p>

      <h3>Managing Your Cookie Preferences</h3>
      <p>
        Use the cookie preferences control in the Site footer to accept or withdraw marketing consent, then save your
        preferences. Analytics consent alone does not activate Meta Pixel. Refusing marketing cookies does not prevent
        you from using the Site or submitting forms. Withdrawing consent stops subsequent Pixel events from this Site,
        but does not automatically erase cookies already stored or information previously sent to Meta. You can delete
        existing _fbp and _fbc cookies using your browser settings.
      </p>
      <p>
        <strong>Browser Settings</strong>
      </p>
      <ul>
        <li>View and delete existing cookies</li>
        <li>Block cookies from being set</li>
        <li>Set preferences for specific websites</li>
        <li>Receive notifications when cookies are being set</li>
      </ul>
      <p>
        Disabling certain cookies may affect Site functionality and limit your ability to fully experience our services.
      </p>

      <p>
        <strong>Opt-Out Tools</strong>
      </p>
      <ul>
        <li>
          Digital Advertising Alliance:{" "}
          <a href="http://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer">
            aboutads.info/choices
          </a>
        </li>
        <li>
          European Interactive Digital Advertising Alliance:{" "}
          <a href="http://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer">
            youronlinechoices.eu
          </a>
        </li>
      </ul>

      <p>
        <strong>Cookie Consent Management</strong>
      </p>
      <p>
        When you first visit our Site, we will ask for your consent to use non-essential cookies. Your consent
        preferences will be stored for 12 months, after which we will request your consent again.
      </p>

      <h3>Updates to This Cookie Policy</h3>
      <p>
        We may update this Cookie Policy to reflect changes in our practices or for legal, regulatory, or operational
        reasons. When we make significant changes, we will post the updated policy on our Site, update the "Last
        Updated" date, provide prominent notice, and when appropriate email registered users.
      </p>

      <h3>Contact Us</h3>
      <p>
        <strong>Palamar Teknoloji Anonim Şirketi</strong>
      </p>
      <p>Address: Teknopark Istanbul, 1/10C Z42 Pendik/Istanbul</p>
      <p>
        Email: <a href="mailto:privacy@palmate.ai">privacy@palmate.ai</a>
      </p>
      <p>
        Website:{" "}
        <a href="https://www.palmate.ai" target="_blank" rel="noopener noreferrer">
          www.palmate.ai
        </a>
      </p>
      <p>For more detailed information about our data practices, please refer to our Privacy Policy.</p>

      <h3>Additional Resources</h3>
      <ul>
        <li>
          <a href="https://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer">
            All About Cookies
          </a>
        </li>
        <li>
          <a
            href="https://wikis.ec.europa.eu/display/WEBGUIDE/04.+Cookies+and+similar+technologies"
            target="_blank"
            rel="noopener noreferrer"
          >
            European Commission Cookie Guidelines
          </a>
        </li>
        <li>
          <a
            href="https://www.dataprotection.ie/sites/default/files/uploads/2020-04/Guidance%20note%20on%20cookies%20and%20other%20tracking%20technologies.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Data Protection Authority Cookie Guidance
          </a>
        </li>
      </ul>
    </div>
  );
}

export function CookiePolicyTR() {
  return (
    <div className={styles.policy}>
      <p>
        <strong>Son Güncelleme: 16 Eylül 2026</strong>
      </p>

      <p>
        Palamar Teknoloji Anonim Şirketi (“Palmate AI”, “biz”){" "}
        <a href="https://www.palmate.ai" target="_blank" rel="noopener noreferrer">
          www.palmate.ai
        </a>{" "}
        adresindeki web sitemizde (“Site”) ziyaretlerinize ilişkin bazı bilgileri hatırlamamıza ve yapay zekâ destekli
        sohbet botu çözümlerimizle deneyiminizi geliştirmemize yardımcı olmak için çerezler ve benzeri teknolojiler
        kullanır. Bu Çerez Politikası, çerezleri nasıl kullandığımızı, hangi tür çerezleri yerleştirdiğimizi ve çerez
        tercihlerinizi nasıl yönetebileceğinizi açıklar. Gizlilik Politikamız ile birlikte okunmalıdır.
      </p>

      <h3>Çerez Nedir?</h3>
      <p>
        Çerezler, bir web sitesini ziyaret ettiğinizde bilgisayarınıza veya mobil cihazınıza yerleştirilen küçük metin
        dosyalarıdır. Zaman içinde eylemlerinizi ve tercihlerinizi hatırlayarak gezinme deneyiminizi daha verimli ve
        kişiselleştirilmiş hâle getirir. Çerezler, Sitemizin cihazınızı tanımasına ve size daha iyi bir kullanıcı
        deneyimi sunmasına imkân verir.
      </p>
      <p>
        Sitemizle etkileşime girdiğinizde web sunucumuz cihazınıza çerezler gönderebilir. Bu çerezler ziyaretinize
        ilişkin veriler içerebilir ve aşağıdakilere yardımcı olur:
      </p>
      <ul>
        <li>Tercihlerinizi ve ayarlarınızı hatırlamak</li>
        <li>Hizmetlerimizi nasıl kullandığınızı anlamak</li>
        <li>Kişiselleştirilmiş içerik ve öneriler sunmak</li>
        <li>YZ sohbet botu çözümlerimizin performansını iyileştirmek</li>
      </ul>

      <h3>Çerez Kullanıyor muyuz ve Amaçlarımız Neler?</h3>
      <p>
        Evet. Sitemizde ve YZ sohbet botu hizmetlerimizde deneyiminizi geliştirmek için çerez kullanıyoruz. Amaçlarımız:
      </p>
      <p>
        <strong>Web Sitesi İşlevselliği</strong>: Kullanıcıları tanımlamak, talepleri işlemek, tercihleri hatırlamak ve
        sayfalar arasında gezinirken bilgileri tekrar girmeden hareket etmenizi sağlamak.
      </p>
      <p>
        <strong>Analitik ve Performans</strong>: Site kullanımı, sohbet botlarıyla etkileşim kalıpları ve sistem
        performansı hakkında veri toplamak ve analiz etmek.
      </p>
      <p>
        <strong>Kişiselleştirme</strong>: İlgi alanlarınıza ve önceki etkileşimlerinize göre içerik ve öneriler sunmak.
      </p>
      <p>
        <strong>Pazarlama ve Reklam</strong>: İş ortaklarımızın sitelerinde çözümlerimizi tanıtmak, ilgi alanlarına
        uygun içerik göstermek ve kampanya etkinliğini ölçmek.
      </p>
      <p>
        <strong>Güvenlik</strong>: Sahteciliği önlemek ve hesabınıza güvenli erişim sağlamak.
      </p>

      <h3>Kullandığımız Çerez Türleri</h3>
      <p>
        <strong>Kaynağına Göre</strong>
      </p>
      <p>
        <strong>Birinci Taraf Çerezleri</strong>: Doğrudan Palmate AI tarafından alan adımızda yerleştirilir. Temel
        işlevsellik, kimlik doğrulama ve kişiselleştirme sağlar.
      </p>
      <p>
        <strong>Üçüncü Taraf Çerezleri</strong>: Analitik platformları, reklam ağları ve müşteri destek araçları gibi
        harici sağlayıcılar tarafından yerleştirilir.
      </p>
      <p>
        <strong>Ziyaretçi Tanımlama Hakkında Not:</strong> Cihazınıza çerez yerleştirerek ziyaretçileri tanımlayan ve
        gezinme etkinliğinizi e-posta adresiniz dahil diğer kişisel bilgilerle ilişkilendiren üçüncü taraf hizmetler
        kullanıyoruz. Bu bilgiler size kişiselleştirilmiş iletişim ve pazarlama göndermek için kullanılabilir. Bu
        izlemeden{" "}
        <a href="https://app.retention.com/optout" target="_blank" rel="noopener noreferrer">
          https://app.retention.com/optout
        </a>{" "}
        adresinden vazgeçebilirsiniz. GDPR uyumlu vazgeçme için{" "}
        <a href="https://www.rb2b.com/rb2b-gdpr-opt-out" target="_blank" rel="noopener noreferrer">
          https://www.rb2b.com/rb2b-gdpr-opt-out
        </a>{" "}
        adresini ziyaret edin.
      </p>

      <p>
        <strong>Süresine Göre</strong>
      </p>
      <p>
        <strong>Oturum Çerezleri</strong>: Tarayıcıyı kapattığınızda silinir. Gezinme ve oturumun sürdürülmesine
        yardımcı olur.
      </p>
      <p>
        <strong>Kalıcı Çerezler</strong>: Belirli bir süre cihazınızda kalır veya siz silene kadar saklanır. Ziyaretler
        arasında tercihlerinizi hatırlar.
      </p>

      <p>
        <strong>Amacına Göre</strong>
      </p>
      <p>
        <strong>Zorunlu Çerezler</strong>: Sitemizin ve YZ hizmetlerimizin temel çalışması için gereklidir. Güvenli
        giriş ve sohbet botu etkileşimleri bu çerezler olmadan düzgün çalışmaz.
      </p>
      <p>
        <strong>Analitik Çerezler</strong>: Ziyaretçilerin Site ve YZ çözümleriyle nasıl etkileştiğini anlamamıza
        yardımcı olur.
      </p>
      <p>
        <strong>Kişiselleştirme Çerezleri</strong>: Tercihlerinizi hatırlayarak kişiselleştirilmiş deneyimler sunar.
      </p>
      <p>
        <strong>Pazarlama Çerezleri</strong>: İlgili reklamları sunmak ve kampanya etkinliğini ölçmek için kullanılır.
      </p>

      <h3>Çerezsiz Ölçüm</h3>
      <p>
        Çerez tercihinizden bağımsız olarak sayfa görüntülemelerini kendi sunucularımızda, çerez veya cihazınıza yazılan herhangi
        bir tanımlayıcı kullanmadan sayarız. Sayfa adresi, yönlendiren site, kampanya parametreleri, cihaz türü ve dil bilgisi,
        günlük değişen anonim bir özet değerle birlikte tutulur; IP adresi saklanmaz ve üçüncü taraflarla paylaşılmaz. Bu ölçüm
        sizi tanımlamaz ve reklam amacıyla kullanılmaz.
      </p>
      <h3>Kullandığımız Çerezler</h3>
      <p>Aşağıdaki tablo, Sitemizde kullanılan başlıca çerez kategorilerini listeler:</p>
      <table>
        <thead>
          <tr>
            <th>Çerez Adı</th>
            <th>Kaynak</th>
            <th>Amaç</th>
            <th>Süre</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>_palmate_session</td>
            <td>Palmate AI</td>
            <td>Zorunlu - Oturum yönetimi</td>
            <td>Oturum</td>
          </tr>
          <tr>
            <td>_palmate_user</td>
            <td>Palmate AI</td>
            <td>Zorunlu - Kullanıcı tanımlama</td>
            <td>30 gün</td>
          </tr>
          <tr>
            <td>_ga, _ga_*</td>
            <td>Google Analytics</td>
            <td>Analitik - Kullanım takibi</td>
            <td>1–2 yıl</td>
          </tr>
          <tr>
            <td>_gid</td>
            <td>Google Analytics</td>
            <td>Analitik - Oturum takibi</td>
            <td>24 saat</td>
          </tr>
          <tr>
            <td>_gat</td>
            <td>Google Analytics</td>
            <td>Analitik - İstek sınırlandırma</td>
            <td>1 dakika</td>
          </tr>
          <tr>
            <td>_gcl_au</td>
            <td>Google Ads</td>
            <td>Pazarlama - Dönüşüm ilişkilendirme</td>
            <td>90 gün</td>
          </tr>
          <tr>
            <td>_fbp</td>
            <td>Meta Pixel</td>
            <td>Pazarlama - Tarayıcı tanımlama ve reklam ölçümü</td>
            <td>90 gün</td>
          </tr>
          <tr>
            <td>_fbc</td>
            <td>Meta Pixel</td>
            <td>Pazarlama - Meta tıklaması tanımlama ve dönüşüm ilişkilendirme</td>
            <td>90 gün</td>
          </tr>
          <tr>
            <td>_linkedin_tracking</td>
            <td>LinkedIn</td>
            <td>Pazarlama - Kampanya ölçümü</td>
            <td>30 gün</td>
          </tr>
          <tr>
            <td>hubspotutk</td>
            <td>HubSpot</td>
            <td>Pazarlama - Potansiyel müşteri takibi</td>
            <td>13 ay</td>
          </tr>
          <tr>
            <td>_clck, _clsk</td>
            <td>Microsoft Clarity</td>
            <td>Analitik - Kullanıcı davranışı</td>
            <td>1 yıl</td>
          </tr>
          <tr>
            <td>__cf_bm</td>
            <td>Cloudflare</td>
            <td>Zorunlu - Bot yönetimi</td>
            <td>30 dakika</td>
          </tr>
          <tr>
            <td>personalization_prefs</td>
            <td>Palmate AI</td>
            <td>Kişiselleştirme - Arayüz tercihleri</td>
            <td>1 yıl</td>
          </tr>
          {/* <tr>
            <td>reb2b</td>
            <td>RB2B / Retention.com</td>
            <td>Pazarlama - Ziyaretçi tanımlama ve izleme</td>
            <td>1 yıl</td>
          </tr> */}
        </tbody>
      </table>
      <p>
        <em>
          Not: Bu liste başlıca çerezleri içerir ve kapsamlı olmayabilir. Platformumuza entegre üçüncü taraf hizmetler
          ek çerezler yerleştirebilir.
        </em>
      </p>

      <h3>Meta Pixel ve Pazarlama Çerezleri</h3>
      <p>
        Meta Pixel (Facebook ve Instagram), palmate.ai üzerinde pazarlama çerezlerini yalnızca pazarlama izni vermenizden
        sonra kullanır. _fbp çerezi, reklam ölçümü için tarayıcıyı tanımlar. _fbc çerezi, sayfa adresinde bir Meta tıklama
        tanımlayıcısı (fbclid) bulunduğunda bunu saklayarak sonraki olayları ilgili tıklamayla ilişkilendirir; her ziyaretçi
        için oluşturulmaz. İkisi de alan adımızda tutulan birinci taraf çerezlerdir. Olağan süreleri 90 gündür ve sonraki
        ziyaretlerde yenilenebilir; tarayıcı kısıtlamaları veya silme işlemi bu süreyi kısaltabilir.
      </p>
      <p>
        İzin verdiğinizde Pixel, kampanya ölçümü ve ilişkilendirme için sayfa görüntülemelerini ve başarıyla gönderilen
        demo, teklif veya iletişim taleplerini Meta'ya iletir. Olay verileri ve Meta'nın bunları kullanımı Gizlilik
        Politikamızda açıklanmıştır. Çerez süreleri, Meta'nın aldığı bilgileri saklama süresi anlamına gelmez.
        Sağlayıcının çerez kullanımı ve kontrol seçenekleri için{" "}
        <a href="https://www.facebook.com/privacy/policies/cookies/" target="_blank" rel="noopener noreferrer">Meta Çerezler İlkesi</a>{" "}
        metnini inceleyebilirsiniz.
      </p>

      <h3>Çerez Tercihlerinin Yönetimi</h3>
      <p>
        Pazarlama izni vermek veya izninizi geri çekmek için sitenin alt bölümündeki çerez tercihleri kontrolünü kullanın
        ve tercihlerinizi kaydedin. Yalnızca analitik izni Meta Pixel'i etkinleştirmez. Pazarlama çerezlerini reddetmeniz
        siteyi kullanmanızı veya form göndermenizi engellemez. İzninizi geri çekmeniz bu siteden sonraki Pixel olaylarının
        gönderilmesini durdurur; önceden kaydedilmiş çerezleri veya Meta'ya daha önce iletilen bilgileri kendiliğinden
        silmez. Mevcut _fbp ve _fbc çerezlerini tarayıcı ayarlarınızdan silebilirsiniz.
      </p>
      <p>
        <strong>Tarayıcı Ayarları</strong>
      </p>
      <ul>
        <li>Mevcut çerezleri görüntüleme ve silme</li>
        <li>Çerezlerin ayarlanmasını engelleme</li>
        <li>Belirli siteler için tercih belirleme</li>
        <li>Çerez ayarlanırken bildirim alma</li>
      </ul>
      <p>
        Bazı çerezlerin devre dışı bırakılması Site işlevlerini etkileyebilir ve hizmetlerimizden tam olarak
        yararlanmanızı sınırlayabilir.
      </p>

      <p>
        <strong>Vazgeçme Araçları</strong>
      </p>
      <ul>
        <li>
          Digital Advertising Alliance:{" "}
          <a href="http://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer">
            aboutads.info/choices
          </a>
        </li>
        <li>
          European Interactive Digital Advertising Alliance:{" "}
          <a href="http://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer">
            youronlinechoices.eu
          </a>
        </li>
      </ul>

      <p>
        <strong>Çerez Onay Yönetimi</strong>
      </p>
      <p>
        Siteyi ilk ziyaret ettiğinizde zorunlu olmayan çerezler için onayınız istenir. Onay tercihlerinizi 12 ay boyunca
        saklarız. Bu sürenin sonunda onayınızı yeniden talep ederiz.
      </p>

      <h3>Bu Çerez Politikasındaki Güncellemeler</h3>
      <p>
        Uygulamalarımızdaki değişiklikleri veya hukuki, düzenleyici ya da operasyonel nedenleri yansıtmak üzere bu Çerez
        Politikasını güncelleyebiliriz. Önemli değişikliklerde Site üzerinde güncellenmiş metni yayımlar, “Son
        Güncelleme” tarihini değiştirir, belirgin bir bildirim sunar ve uygun olduğunda kayıtlı kullanıcılara e-posta
        gönderebiliriz.
      </p>

      <h3>İletişim</h3>
      <p>
        <strong>Palamar Teknoloji Anonim Şirketi</strong>
      </p>
      <p>Adres: Teknopark Istanbul, 1/10C Z42 Pendik/Istanbul</p>
      <p>
        E-posta: <a href="mailto:privacy@palmate.ai">privacy@palmate.ai</a>
      </p>
      <p>
        Web:{" "}
        <a href="https://www.palmate.ai" target="_blank" rel="noopener noreferrer">
          www.palmate.ai
        </a>
      </p>
      <p>Veri uygulamalarımıza ilişkin ayrıntılar için lütfen Gizlilik Politikamıza bakın.</p>

      <h3>Ek Kaynaklar</h3>
      <ul>
        <li>
          <a href="https://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer">
            All About Cookies
          </a>
        </li>
        <li>
          <a
            href="https://wikis.ec.europa.eu/display/WEBGUIDE/04.+Cookies+and+similar+technologies"
            target="_blank"
            rel="noopener noreferrer"
          >
            Avrupa Komisyonu Çerez Rehberi
          </a>
        </li>
        <li>
          <a
            href="https://www.dataprotection.ie/sites/default/files/uploads/2020-04/Guidance%20note%20on%20cookies%20and%20other%20tracking%20technologies.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Veri Koruma Otoritesi Çerez Rehberi
          </a>
        </li>
      </ul>
    </div>
  );
}
